import dayjs from "dayjs";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { SEASON_API_STATUS } from "../../../../types/gamePass";
import {
  enrichProgressTasksFromEntry,
  getChallengeCandidateIds,
  getChallengeProgressEntryKey,
  getProgressEntryChallengeIds,
  isProgressChallengeCompleted,
  getManualPassReason,
  isManualPassIndicator,
  isProgressTaskCompleted,
  isTaskKeyedProgressEntry,
  normalizeCompletedFlag,
  progressEntryMatchesConfiguredTask,
  valuesMatch,
} from "../../../../queries/game-pass/userProgressMappers";

export const COMPLETED_SEASONS_PREVIEW_COUNT = 5;

const toDisplayValue = (value) => (value == null ? 0 : value);

const compareCompletedSeasonsByStartDate = (left, right) => {
  const leftTime = dayjs(left.start_date).valueOf();
  const rightTime = dayjs(right.start_date).valueOf();
  if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) {
    return 0;
  }
  return rightTime - leftTime;
};

export const getSeasonRecordId = (season) => season?.id ?? season?.season_id ?? "";

export const GAME_PASS_NOT_ENROLLED_ERROR_MESSAGE =
  "User not enrolled in this season";

const getGamePassApiErrorMessage = (error) => {
  const responseData = error?.response?.data;
  if (!responseData || typeof responseData !== "object") {
    return "";
  }

  return String(responseData.error ?? responseData.message ?? "");
};

export const isGamePassNotEnrolledError = (error) =>
  error?.response?.status === 404 &&
  getGamePassApiErrorMessage(error) === GAME_PASS_NOT_ENROLLED_ERROR_MESSAGE;

export const isGamePassNotEnrolledQuery = (query) =>
  Boolean(
    query &&
      !query.isLoading &&
      !query.isFetching &&
      query.isError &&
      isGamePassNotEnrolledError(query.error),
  );

// Callers must exclude activeSeasonId from the batch progress map; this merge
// overwrites any existing entry for that season with the dedicated query result.
export const mergeActiveSeasonProgressIntoMap = (
  seasonProgressById = {},
  activeSeasonId,
  activeSeasonProgress,
) => {
  if (!activeSeasonId || !activeSeasonProgress) {
    return seasonProgressById;
  }

  return {
    ...seasonProgressById,
    [activeSeasonId]: activeSeasonProgress,
  };
};

export const isUserEnrolledInProgressResponse = (progressResponse) => {
  const progressData = progressResponse?.data;
  if (!progressData) {
    return false;
  }

  const progressEntries = Array.isArray(progressData.progress)
    ? progressData.progress
    : [];
  if (progressEntries.length > 0) {
    return true;
  }

  const enrollment = progressData.enrollment;
  if (!enrollment) {
    return false;
  }

  return Boolean(enrollment.user_id || enrollment.enrolled_at != null);
};

export const filterEnrolledAccordionSeasons = (seasons = [], enrolledSeasonIds) => {
  const enrolledIds = enrolledSeasonIds ?? new Set();

  return (seasons ?? []).filter((season) =>
    enrolledIds.has(getSeasonRecordId(season)),
  );
};

export const getEnrolledSeasonIdsFromProgressMap = (seasonProgressById = {}) => {
  const enrolledIds = new Set();

  Object.entries(seasonProgressById).forEach(([seasonId, progressResponse]) => {
    if (isUserEnrolledInProgressResponse(progressResponse)) {
      enrolledIds.add(seasonId);
    }
  });

  return enrolledIds;
};

export const partitionAccordionSeasons = (seasons = []) => {
  const eligibleSeasons = (seasons ?? []).filter((season) =>
    [SEASON_API_STATUS.ACTIVE, SEASON_API_STATUS.COMPLETED].includes(
      season.status,
    ),
  );

  const activeSeasons = eligibleSeasons.filter(
    (season) => season.status === SEASON_API_STATUS.ACTIVE,
  );
  const completedSeasons = eligibleSeasons
    .filter((season) => season.status === SEASON_API_STATUS.COMPLETED)
    .sort(compareCompletedSeasonsByStartDate);

  return { activeSeasons, completedSeasons };
};

export const getVisibleAccordionSeasons = (
  seasons = [],
  showAllSeasons = false,
  previewCount = COMPLETED_SEASONS_PREVIEW_COUNT,
) => {
  const { activeSeasons, completedSeasons } = partitionAccordionSeasons(seasons);
  const visibleCompleted = showAllSeasons
    ? completedSeasons
    : completedSeasons.slice(0, previewCount);

  return [...activeSeasons, ...visibleCompleted];
};

export const hasHiddenAccordionSeasons = (
  seasons = [],
  previewCount = COMPLETED_SEASONS_PREVIEW_COUNT,
) =>
  partitionAccordionSeasons(seasons).completedSeasons.length > previewCount;

const isTaskCompleted = isProgressTaskCompleted;
const isChallengeMarkedCompleted = isProgressChallengeCompleted;

const countCompletedChallenges = (progressEntries = []) =>
  progressEntries.filter((entry) => isChallengeMarkedCompleted(entry)).length;

const countTaskProgress = (progressEntries = []) =>
  progressEntries.reduce(
    (totals, entry) => {
      const tasks = entry?.tasks ?? [];
      return {
        completedTasks:
          totals.completedTasks + tasks.filter(isTaskCompleted).length,
        totalTasks: totals.totalTasks + tasks.length,
      };
    },
    { completedTasks: 0, totalTasks: 0 },
  );

export const buildSeasonProgressSummary = ({
  progressEntries = [],
  totalChallenges,
  totalTasks,
}) => {
  const completedChallenges = countCompletedChallenges(progressEntries);
  const taskProgress = countTaskProgress(progressEntries);
  const resolvedTotalChallenges =
    totalChallenges ?? progressEntries.length ?? 0;
  const resolvedTotalTasks = totalTasks ?? taskProgress.totalTasks;
  const completedTasks = taskProgress.completedTasks;
  const percent =
    resolvedTotalChallenges > 0
      ? Math.round((completedChallenges / resolvedTotalChallenges) * 100)
      : 0;

  return {
    completedChallenges,
    totalChallenges: resolvedTotalChallenges,
    completedTasks,
    totalTasks: resolvedTotalTasks,
    percent,
  };
};

export const getChallengeRecordId = (challenge, index = 0) =>
  challenge?.id ?? challenge?.challenge_id ?? String(index);

const taskBelongsToChallenge = (task, challenge) => {
  const challengeId = challenge?.id ?? challenge?.challenge_id;

  if (task.challenge_id) {
    return task.challenge_id === challengeId;
  }

  // `challenge_order` is the task's position *within* its challenge (the sort
  // key), not the challenge's position — so it can't be used to associate a
  // task with a challenge. Fall back to the challenge display name only.
  return task.challenge === challenge?.name;
};

export const countConfiguredTasksForChallenge = (challenge, seasonTasks = []) =>
  getTasksForChallenge(challenge, seasonTasks).length;

export const getTaskRecordId = (task, index) => task?.id ?? `task-${index}`;

export const getTasksForChallenge = (challenge, seasonTasks = []) =>
  seasonTasks
    .filter((task) => taskBelongsToChallenge(task, challenge))
    .sort((left, right) => {
      const leftTime = dayjs(left.created_at).valueOf();
      const rightTime = dayjs(right.created_at).valueOf();
      if (
        Number.isFinite(leftTime) &&
        Number.isFinite(rightTime) &&
        leftTime !== rightTime
      ) {
        return leftTime - rightTime;
      }

      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });

const getProgressTaskIdentifier = (progressTask) =>
  progressTask?.task_id ?? progressTask?.id ?? null;

export const getTaskProgressForConfiguredTask = (
  challengeProgressEntry,
  configuredTask,
  displayIndex,
  { progressEntries = [], seasonIds = [] } = {},
) => {
  const progressTasks = challengeProgressEntry?.tasks ?? [];
  const configuredTaskId = configuredTask?.id;

  if (configuredTaskId) {
    const matchedById = progressTasks.find(
      (progressTask) => getProgressTaskIdentifier(progressTask) === configuredTaskId,
    );
    if (matchedById) {
      return matchedById;
    }
  }

  const configuredTaskName = configuredTask?.name?.trim();
  if (configuredTaskName) {
    const matchedByName = progressTasks.find((progressTask) => {
      const progressTaskName = progressTask?.name ?? progressTask?.task_name;
      return (
        typeof progressTaskName === "string" &&
        progressTaskName.trim() === configuredTaskName
      );
    });
    if (matchedByName) {
      return matchedByName;
    }
  }

  if (!configuredTaskId) {
    const matchedByIndexField = progressTasks.filter(
      (progressTask) => progressTask?.index === displayIndex,
    );
    if (matchedByIndexField.length === 1) {
      return matchedByIndexField[0];
    }

    const matchedByDisplayIndex = progressTasks[displayIndex] ?? null;
    if (matchedByDisplayIndex && matchedByIndexField.length === 0) {
      return matchedByDisplayIndex;
    }

    return null;
  }

  if (progressEntries.length > 0) {
    for (const entry of progressEntries) {
      if (!progressEntryMatchesConfiguredTask(entry, seasonIds, configuredTaskId)) {
        continue;
      }

      const enrichedTasks = enrichProgressTasksFromEntry(entry);
      const matchedTask = enrichedTasks.find(
        (progressTask) =>
          getProgressTaskIdentifier(progressTask) === configuredTaskId,
      );

      if (matchedTask) {
        return matchedTask;
      }
    }
  }

  const progressTasksHaveIds = progressTasks.some((progressTask) =>
    Boolean(getProgressTaskIdentifier(progressTask)),
  );
  if (!progressTasksHaveIds) {
    const matchedByDisplayOrder = progressTasks[displayIndex] ?? null;
    if (matchedByDisplayOrder) {
      return matchedByDisplayOrder;
    }
  }

  return null;
};

/** @deprecated Prefer getTaskProgressForConfiguredTask for stable task matching. */
export const getTaskProgressAtIndex = (challengeProgressEntry, index) =>
  getTaskProgressForConfiguredTask(challengeProgressEntry, null, index);

export const formatTaskRewardLabel = (task) => {
  const xpReward = task?.xp_reward;
  if (xpReward != null && xpReward > 0) {
    return `${xpReward}${AppStrings.XP}`;
  }

  return AppStrings.NOT_AVAILABLE;
};

export const buildTaskCompletionStatus = (progressTask) => {
  if (!isTaskCompleted(progressTask)) {
    return { label: "False", variant: "incomplete" };
  }

  const isManualPass =
    normalizeCompletedFlag(progressTask?.manually_passed) === true ||
    isManualPassIndicator(progressTask);

  if (isManualPass) {
    return {
      label: AppStrings.MANUAL_PASS,
      variant: "manual-pass",
      reason: getManualPassReason(progressTask),
    };
  }

  return { label: "True", variant: "complete" };
};

export const findChallengeProgressEntry = (
  progressEntries = [],
  seasonIds = [],
  challenge,
  challengeId,
  challengeIndex,
) => {
  const candidateIds = getChallengeCandidateIds(
    challenge,
    challengeId,
    challengeIndex,
  );
  if (candidateIds.length === 0) {
    return null;
  }

  const normalizedSeasonIds = [...new Set(seasonIds.filter(Boolean))];

  return (
    progressEntries.find((entry) => {
      const entryChallengeIds = getProgressEntryChallengeIds(entry);

      if (
        [...entryChallengeIds].some((entryId) =>
          candidateIds.some((candidateId) => valuesMatch(candidateId, entryId)),
        )
      ) {
        return true;
      }

      return normalizedSeasonIds.some((seasonId) =>
        candidateIds.some(
          (id) => entry?.["season_id#challenge_id"] === getChallengeProgressEntryKey(seasonId, id),
        ),
      );
    }) ?? null
  );
};

const countCompletedConfiguredTasks = (
  progressEntry,
  challenge,
  seasonTasks = [],
  progressEntries = [],
  seasonIds = [],
) => {
  const configuredTasks = getTasksForChallenge(challenge, seasonTasks);
  if (configuredTasks.length === 0) {
    return null;
  }

  const taskLookupContext = { progressEntries, seasonIds };

  return {
    completedTasks: configuredTasks.filter((task, index) =>
      isTaskCompleted(
        getTaskProgressForConfiguredTask(
          progressEntry,
          task,
          index,
          taskLookupContext,
        ),
      ),
    ).length,
    totalTasks: configuredTasks.length,
  };
};

export const mergeChallengeProgressEntries = (entries = []) => {
  if (entries.length === 0) {
    return null;
  }

  if (entries.length === 1) {
    return {
      ...entries[0],
      tasks: enrichProgressTasksFromEntry(entries[0]),
    };
  }

  const enrichedTasks = entries.flatMap((entry) =>
    enrichProgressTasksFromEntry(entry),
  );
  const dedupedTasks = [];
  const seenTaskIds = new Set();

  enrichedTasks.forEach((task) => {
    const taskId = getProgressTaskIdentifier(task);
    const dedupeKey =
      taskId ?? `${task?.index ?? "na"}-${task?.completed_at ?? "na"}`;
    if (seenTaskIds.has(dedupeKey)) {
      return;
    }
    seenTaskIds.add(dedupeKey);
    dedupedTasks.push(task);
  });

  const allComplete = entries.every((entry) => isChallengeMarkedCompleted(entry));
  const completedTimestamps = entries
    .map((entry) => entry.completed_at ?? 0)
    .filter((value) => value > 0);

  return {
    ...entries[entries.length - 1],
    tasks: dedupedTasks,
    completed: allComplete,
    completed_at: allComplete
      ? Math.max(...completedTimestamps, 0) || null
      : entries[entries.length - 1]?.completed_at ?? null,
  };
};

const getProgressEntriesForChallenge = (
  challenge,
  challengeId,
  challengeIndex,
  progressEntries = [],
  seasonIds = [],
  seasonTasks = [],
) => {
  const configuredTasks = getTasksForChallenge(challenge, seasonTasks);
  const configuredTaskIds = configuredTasks
    .map((task) => task?.id)
    .filter(Boolean);

  const taskKeyedEntries = progressEntries.filter((entry) =>
    configuredTaskIds.some((taskId) =>
      isTaskKeyedProgressEntry(entry, seasonIds, taskId),
    ),
  );

  if (taskKeyedEntries.length > 0) {
    return taskKeyedEntries;
  }

  return progressEntries.filter((entry) =>
    findChallengeProgressEntry(
      [entry],
      seasonIds,
      challenge,
      challengeId,
      challengeIndex,
    ),
  );
};

export const buildChallengeProgressEntryMap = (
  challenges = [],
  progressEntries = [],
  seasonIds = [],
  seasonTasks = [],
) => {
  const sortedChallenges = [...challenges].sort(
    (left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0),
  );
  const entryMap = new Map();
  const usedEntries = new Set();

  sortedChallenges.forEach((challenge, index) => {
    const challengeId = getChallengeRecordId(challenge, index);
    const availableEntries = progressEntries.filter(
      (entry) => !usedEntries.has(entry),
    );
    const matchedEntries = getProgressEntriesForChallenge(
      challenge,
      challengeId,
      index,
      availableEntries,
      seasonIds,
      seasonTasks,
    );

    if (matchedEntries.length === 0) {
      return;
    }

    matchedEntries.forEach((entry) => usedEntries.add(entry));
    entryMap.set(
      challengeId,
      mergeChallengeProgressEntries(matchedEntries),
    );
  });

  const unmatchedChallenges = sortedChallenges.filter((challenge, index) => {
    const challengeId = getChallengeRecordId(challenge, index);
    return !entryMap.has(challengeId);
  });
  const unmatchedEntries = progressEntries.filter(
    (entry) => !usedEntries.has(entry),
  );

  if (
    seasonTasks.length === 0 &&
    unmatchedChallenges.length > 0 &&
    unmatchedChallenges.length === unmatchedEntries.length
  ) {
    unmatchedChallenges.forEach((challenge, index) => {
      const challengeIndex = sortedChallenges.indexOf(challenge);
      const challengeId = getChallengeRecordId(challenge, challengeIndex);
      entryMap.set(challengeId, unmatchedEntries[index]);
    });
  }

  return entryMap;
};

export const buildChallengeProgressSummary = (
  progressEntry,
  {
    configuredTaskCount = 0,
    challengeIndex,
    enrollment,
    challenge,
    seasonTasks,
    progressEntries = [],
    seasonIds = [],
  } = {},
) => {
  const configuredCounts =
    challenge != null
      ? countCompletedConfiguredTasks(
          progressEntry,
          challenge,
          seasonTasks,
          progressEntries,
          seasonIds,
        )
      : null;

  const tasks = progressEntry?.tasks ?? [];
  const progressTaskCount = tasks.length;
  const completedFromTasks = tasks.filter(isTaskCompleted).length;
  const totalTasks = configuredCounts
    ? configuredCounts.totalTasks
    : progressTaskCount > 0
      ? progressTaskCount
      : configuredTaskCount;
  let completedTasks = configuredCounts
    ? configuredCounts.completedTasks
    : completedFromTasks;

  const hasConfiguredTaskDenominator =
    configuredCounts != null || configuredTaskCount > 0;

  let isCompleted = totalTasks > 0 && completedTasks === totalTasks;

  if (!hasConfiguredTaskDenominator) {
    isCompleted =
      isChallengeMarkedCompleted(progressEntry) ||
      (totalTasks > 0 && completedTasks === totalTasks);

    const currentChallengeIndex = enrollment?.current_challenge_index;
    if (
      !isCompleted &&
      typeof challengeIndex === "number" &&
      typeof currentChallengeIndex === "number" &&
      challengeIndex < currentChallengeIndex
    ) {
      isCompleted = true;
      if (totalTasks > 0) {
        completedTasks = totalTasks;
      }
    }
  }

  return {
    completedTasks,
    totalTasks,
    progressLabel:
      totalTasks > 0 ? `${completedTasks}/${totalTasks}` : null,
    status: isCompleted ? "completed" : "in_progress",
    statusLabel: isCompleted ? "Completed" : "In Progress",
  };
};

export const buildSeasonProgressSummaryFromProgress = ({
  progressEntries = [],
  enrollment,
  totalChallenges,
  totalTasks,
}) => {
  const sortedEntries = [...progressEntries].sort(
    (left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0),
  );

  let completedChallenges = 0;
  let completedTasks = 0;
  let aggregatedTotalTasks = 0;

  sortedEntries.forEach((progressEntry, index) => {
    const challengeSummary = buildChallengeProgressSummary(progressEntry, {
      challengeIndex: index,
      enrollment,
    });

    if (challengeSummary.status === "completed") {
      completedChallenges += 1;
    }

    completedTasks += challengeSummary.completedTasks;
    aggregatedTotalTasks += challengeSummary.totalTasks;
  });

  const resolvedTotalChallenges =
    totalChallenges ?? sortedEntries.length ?? 0;
  const resolvedTotalTasks = totalTasks ?? aggregatedTotalTasks;
  const percent =
    resolvedTotalChallenges > 0
      ? Math.round((completedChallenges / resolvedTotalChallenges) * 100)
      : 0;

  return {
    completedChallenges,
    totalChallenges: resolvedTotalChallenges,
    completedTasks,
    totalTasks: resolvedTotalTasks,
    percent,
  };
};

export const buildSeasonProgressSummaryFromChallenges = ({
  challenges = [],
  progressEntries = [],
  seasonTasks = [],
  seasonIds = [],
  enrollment,
  totalChallenges,
  totalTasks,
}) => {
  const sortedChallenges = [...challenges].sort(
    (left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0),
  );
  const challengeProgressEntryMap = buildChallengeProgressEntryMap(
    sortedChallenges,
    progressEntries,
    seasonIds,
    seasonTasks,
  );

  let completedChallenges = 0;
  let completedTasks = 0;
  let aggregatedTotalTasks = 0;

  sortedChallenges.forEach((challenge, index) => {
    const challengeId = getChallengeRecordId(challenge, index);
    const challengeProgressEntry =
      challengeProgressEntryMap.get(challengeId) ?? null;
    const configuredTaskCount = countConfiguredTasksForChallenge(
      challenge,
      seasonTasks,
    );
    const challengeSummary = buildChallengeProgressSummary(
      challengeProgressEntry,
      {
        configuredTaskCount,
        challengeIndex: index,
        enrollment,
        challenge,
        seasonTasks,
        progressEntries,
        seasonIds,
      },
    );

    if (challengeSummary.status === "completed") {
      completedChallenges += 1;
    }

    completedTasks += challengeSummary.completedTasks;
    aggregatedTotalTasks += challengeSummary.totalTasks;
  });

  const resolvedTotalChallenges =
    totalChallenges ?? sortedChallenges.length ?? 0;
  const resolvedTotalTasks = totalTasks ?? aggregatedTotalTasks;
  const percent =
    resolvedTotalChallenges > 0
      ? Math.round((completedChallenges / resolvedTotalChallenges) * 100)
      : 0;

  return {
    completedChallenges,
    totalChallenges: resolvedTotalChallenges,
    completedTasks,
    totalTasks: resolvedTotalTasks,
    percent,
  };
};

export const getChallengePathBadgeLabel = (challenge) => {
  const pathAvailability = challenge?.path_availability ?? challenge?.path;

  if (pathAvailability === "PREMIUM") {
    return "Premium";
  }
  if (pathAvailability === "FREE") {
    return "Free";
  }
  if (pathAvailability === "BOTH") {
    return "Free & Premium";
  }

  return "";
};

export const getSeasonPathBadgeLabels = (season) => {
  const labels = [];

  if (season?.free_path_enabled === true) {
    labels.push("Free");
  }
  if (season?.premium_path_enabled) {
    labels.push("Premium");
  }

  return labels;
};

export const getSeasonEnrollmentDateMeta = (season, enrollment) => {
  if (!season?.premium_path_enabled || !enrollment) {
    return null;
  }

  const timestamp = enrollment.premium_purchased_at ?? enrollment.enrolled_at;
  if (timestamp == null || timestamp === "") {
    return null;
  }

  return {
    label: "Premium Purchased",
    timestamp,
  };
};

export const buildGamePassSummaryKpis = (statsResponse) => {
  const stats = statsResponse?.data ?? {};

  return [
    {
      id: "active-days",
      title: AppStrings.ACTIVE_DAYS,
      value: toDisplayValue(stats.active_days),
    },
    {
      id: "consecutive-days",
      title: AppStrings.CONSECUTIVE_DAYS,
      value: toDisplayValue(stats.consecutive_days),
    },
    {
      id: "initial-following",
      title: AppStrings.INITIAL_FOLLOWING,
      value: toDisplayValue(stats.initial_following),
    },
    {
      id: "current-following",
      title: AppStrings.CURRENT_FOLLOWING,
      value: toDisplayValue(stats.current_following),
    },
    {
      id: "initial-referrals",
      title: AppStrings.INITIAL_REFERRALS,
      value: toDisplayValue(stats.initial_referrals),
    },
    {
      id: "current-referrals",
      title: AppStrings.CURRENT_REFERRALS,
      value: toDisplayValue(stats.current_referrals),
    },
    {
      id: "p2p-wins",
      title: AppStrings.P2P_WINS,
      value: toDisplayValue(stats.p2p_wins),
    },
    {
      id: "daily-challenges",
      title: AppStrings.DAILY_CHALLENGES,
      value: toDisplayValue(stats.daily_challenges_completed),
    },
  ];
};
